import {FC} from "react"

import {useTranslation} from "@frontend/i18n"
import {activeTheme} from "@frontend/themes/activeTheme"
import {Cost, RemediationType} from "@ri2/db/client"
import {css} from "@styled-system/css"
import {hstack, vstack} from "@styled-system/patterns"

import {DataQualityTag} from "../diy/components/data-quality-tag"

type Props = {
  type: RemediationType
  cost: Cost | null
  onlyShowRange?: boolean
  disclosure?: string | null
  isLoading?: boolean
}

export const Price: FC<Props> = ({
  cost,
  type,
  onlyShowRange,
  disclosure,
  isLoading,
}) => {
  const t = useTranslation()

  if (isLoading) {
    return <PriceLoading type={type} />
  }

  if (!cost) {
    return <NoPrice type={type} />
  }

  const priceString =
    onlyShowRange && cost.range
      ? t("general.priceRangeNoCents", {
          bottom: cost.range.low,
          top: cost.range.high,
        })
      : t(
          type === "diyRemediation" ? "general.price" : "general.priceNoCents",
          {
            price: cost.cost,
          },
        )

  const isDiyWithNoParts = type === "diyRemediation" && cost.cost === 0
  const modelNumberCaption = activeTheme.remediationCopy?.modelNumberCaption
  const priceLabels = activeTheme.remediationCopy?.priceLabels
  const useModelNumberCaption =
    Boolean(modelNumberCaption) &&
    type === "replaceRemediation" &&
    (cost.source.type === "productType" ||
      cost.source.type === "productTypeAndAdditionalDetails")
  const captionColor =
    activeTheme.capabilities.supportingText === "emphasis"
      ? "text.primary"
      : "text.quaternary"
  const priceWeight =
    activeTheme.capabilities.remediationEmphasis === "bold"
      ? 700
      : undefined
  const priceLabel = isDiyWithNoParts
    ? (priceLabels?.doItYourselfNoParts ??
      t("cause.nextStep.priceLabel.doItYourselfNoParts"))
    : type === "replaceRemediation"
      ? (priceLabels?.replace ?? t("cause.nextStep.priceLabel.replace"))
      : type === "difmRemediation"
        ? (priceLabels?.doItForMe ??
          t("cause.nextStep.priceLabel.doItForMe"))
        : (priceLabels?.doItYourself ??
          t("cause.nextStep.priceLabel.doItYourself"))

  return (
    <div
      className={css({
        display: "flex",
        flexDirection: "column",
        gap: 12,
        minHeight: 100,
      })}
    >
      <div
        className={hstack({
          gap: 16,
          alignItems: "center",
          marginBottom: 12,
        })}
      >
        <div
          className={css({
            textStyle: "header.m",
            fontWeight: priceWeight,
            color: "text.primary",
          })}
        >
          {type !== "replaceRemediation" && cost.source.type !== "match" && (
            <span
              className={css({
                color: "text.primary",
              })}
            >
              {"~"}
            </span>
          )}
          {priceString}
        </div>
        {!onlyShowRange && cost.range && (
          <div
            className={vstack({height: 32, gap: 0, alignItems: "flex-start"})}
          >
            <p
              className={css({
                textStyle: "captionRegular",
                fontWeight: priceWeight,
                color: captionColor,
              })}
            >
              {t("cause.nextStep.replace.fullRange")}
            </p>
            <span
              className={css({
                textStyle: "captionRegular",
                fontWeight: priceWeight,
                color: captionColor,
              })}
            >
              {t("general.priceRangeNoCents", {
                bottom: cost.range.low,
                top: cost.range.high,
              })}
            </span>
          </div>
        )}
      </div>
      <DataQualityTag
        source={cost.source}
        overrideColorScheme={
          type === "replaceRemediation" || isDiyWithNoParts
            ? "positive"
            : undefined
        }
        overrideTitle={
          isDiyWithNoParts
            ? t("cause.nextStep.dataQuality.price.noPartsRequired")
            : useModelNumberCaption
              ? modelNumberCaption
              : undefined
        }
        className={css({alignSelf: "flex-start"})}
      />
      <div
        className={css({
          color: captionColor,
          textStyle: "captionRegular",
        })}
      >
        {priceLabel}
      </div>
      {disclosure && (
        <div
          className={css({
            color: captionColor,
            textStyle: "captionRegular",
          })}
        >
          {disclosure}
        </div>
      )}
    </div>
  )
}

const PriceLoading: FC<Pick<Props, "type">> = ({type}) => {
  const t = useTranslation()
  const captionColor =
    activeTheme.capabilities.supportingText === "emphasis"
      ? "text.primary"
      : "text.quaternary"
  const priceLabels = activeTheme.remediationCopy?.priceLabels
  const priceLabel =
    type === "replaceRemediation"
      ? (priceLabels?.replace ?? t("cause.nextStep.priceLabel.replace"))
      : type === "difmRemediation"
        ? (priceLabels?.doItForMe ??
          t("cause.nextStep.priceLabel.doItForMe"))
        : (priceLabels?.doItYourself ??
          t("cause.nextStep.priceLabel.doItYourself"))

  return (
    <div
      className={css({
        display: "flex",
        flexDirection: "column",
        gap: 12,
        minHeight: 100,
      })}
      aria-busy="true"
      aria-live="polite"
    >
      <div
        className={hstack({
          gap: 16,
          alignItems: "center",
          marginBottom: 12,
        })}
      >
        <span
          aria-hidden="true"
          className={css({
            display: "inline-block",
            width: "96px",
            height: "28px",
            borderRadius: "6px",
            backgroundColor: "background.quaternary",
            opacity: 0.7,
          })}
        />
      </div>
      <div
        className={css({
          color: captionColor,
          textStyle: "captionRegular",
        })}
      >
        {priceLabel}
      </div>
    </div>
  )
}

const NoPrice: FC<Pick<Props, "type">> = ({type}) => {
  const t = useTranslation()
  const emptyColor =
    activeTheme.capabilities.supportingText === "emphasis"
      ? "text.primary"
      : "darkGrey"

  return (
    <div
      className={vstack({
        height: 80,
        paddingX: 30,
        paddingY: 10,
        justifyContent: "center",
        textStyle: "caption",
        color: emptyColor,
        borderWidth: 1,
        borderStyle: "dashed",
        borderColor: emptyColor,
        borderRadius: 4,
        textAlign: "center",
      })}
    >
      {t(`cause.nextStep.noPrice.${type}`) as string}
    </div>
  )
}
